import { Box, Button, CircularProgress, Typography, Chip } from "@mui/joy";
import { FormProvider, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { useEffect } from "react";

import { RoleConfiguration } from "../../Create/components";
import { Action } from "./Action";
import { newRoleValidation } from "../../../../../libs/form-validations";
import { Alert } from "../../../../../shared";
import { useApiResponseParser } from "../../../../../hooks";
import { useRolePermissions } from "../../../../../hooks/query";
import { IRole, isDefaultPlatformRole } from "../../../../../interfaces";

interface RoleDetailsProps {
    role: IRole;
    onUpdate: (data: IRole) => Promise<void>;
    onDelete: () => Promise<void>;
    isUpdating: boolean;
    isDeleting: boolean;
    updateError?: unknown;
    canManage?: boolean;
}

export function RoleDetails({
    role,
    onUpdate,
    onDelete,
    isUpdating,
    isDeleting,
    updateError,
    canManage = false,
}: RoleDetailsProps) {
    const navigate = useNavigate();
    const { parseErrorResponseData } = useApiResponseParser();
    const { useListByRole, useAssign } = useRolePermissions();
    const rolePermissionsSvc = useListByRole(role.Id);
    const assignPermissionsSvc = useAssign();
    const isDefault = isDefaultPlatformRole(role);
    const readOnly = isDefault || !canManage;

    const methods = useForm({
        resolver: newRoleValidation,
        defaultValues: {
            Name: role.Name || "",
            Description: role.Description || "",
            Permissions: [] as any[],
        },
        mode: "onChange",
    });

    useEffect(() => {
        const assigned = rolePermissionsSvc.data || [];
        const permissions = assigned.map((row: any) => ({
            Id: row.PermissionId || row.Id,
            Name: row.PermissionName || row.Name,
        }));

        methods.reset({
            Name: role.Name,
            Description: role.Description || "",
            Permissions: permissions,
        });
    }, [role, rolePermissionsSvc.data, methods]);

    const onSubmit = methods.handleSubmit(async (data) => {
        if (readOnly) return;

        await onUpdate({ ...data, Id: role.Id } as IRole);

        const permissionIds = (data.Permissions || [])
            .map((p: any) => p?.Id)
            .filter(Boolean);

        await assignPermissionsSvc.mutateAsync({
            RoleId: role.Id,
            PermissionIds: permissionIds,
        });
    });

    const saving = isUpdating || assignPermissionsSvc.isPending;
    const saveError = updateError || (assignPermissionsSvc.isError ? assignPermissionsSvc.error : undefined);

    if (rolePermissionsSvc.isLoading) {
        return (
            <Box component="div" width="100%" p={3} display="flex" justifyContent="center">
                <CircularProgress size="sm" />
            </Box>
        );
    }

    const subtitle = isDefault
        ? "Platform default roles are view-only and cannot be edited or deleted."
        : canManage
          ? "Update the role information below."
          : "View role information.";

    return (
        <Box component="div" width="100%" p={3} overflow="auto">
            <FormProvider {...methods}>
                <form onSubmit={onSubmit}>
                    <Box component="div" display="flex" flexDirection="column" maxWidth={700}>
                        <Box mb={2}>
                            <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                                <Typography level="title-lg">Role Details</Typography>
                                {isDefault ? (
                                    <Chip size="sm" variant="soft" color="neutral">
                                        Platform default
                                    </Chip>
                                ) : null}
                            </Box>
                            <Typography level="body-sm">{subtitle}</Typography>
                        </Box>

                        <Box mb={2}>
                            <Typography
                                level="body-xs"
                                sx={{ fontWeight: 600, textTransform: "uppercase", color: "text.secondary" }}
                            >
                                Role Identifier
                            </Typography>
                            <Typography level="body-sm" sx={{ mt: 0.5, color: "text.primary" }}>
                                {role.Id}
                            </Typography>
                        </Box>

                        <RoleConfiguration readOnly={readOnly} />

                        <Box display="flex" gap={1.5} justifyContent="flex-start" mt={3}>
                            <Button
                                variant="outlined"
                                color="neutral"
                                onClick={() => navigate("/admin/roles")}
                                sx={{ borderRadius: "4px" }}
                            >
                                {readOnly ? "Back" : "Cancel"}
                            </Button>
                            {!readOnly ? (
                                <Button
                                    type="submit"
                                    disabled={saving || !methods.formState.isDirty}
                                    loading={saving}
                                    loadingPosition="start"
                                    variant="soft"
                                    color="primary"
                                    sx={{ borderRadius: "4px" }}
                                >
                                    Update Role
                                </Button>
                            ) : null}
                        </Box>

                        {!readOnly ? (
                            <>
                                <Alert
                                    successMessage={
                                        !saving &&
                                        methods.formState.isSubmitSuccessful &&
                                        !saveError &&
                                        "Role updated successfully!"
                                    }
                                    errorMessage={saveError && parseErrorResponseData(saveError)}
                                    sx={{ mt: 2 }}
                                />

                                <Action onDelete={onDelete} isDeleting={isDeleting} roleName={role.Name} />
                            </>
                        ) : null}
                    </Box>
                </form>
            </FormProvider>
        </Box>
    );
}
